import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Dropdown } from 'primereact/dropdown';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { dateOnlyBodyTemplate } from '../../utils/dateUtils';

// Mock data for demonstration
const mockShipments = [
    {
        id: 1,
        shipmentNumber: 'SHP-1001',
        orderNumber: 'ORD-7845',
        customerName: 'Mega Motor A.Ş.',
        shipmentDate: '2025-04-05',
        estimatedDeliveryDate: '2025-04-10',
        actualDeliveryDate: '2025-04-09',
        status: 'Delivered',
        carrier: 'Aras Kargo',
        trackingNumber: 'AK123456789',
        totalWeight: 250,
        totalVolume: 1.2,
        totalItems: 15,
        deliveryAddress: 'İstanbul, Türkiye',
        notes: 'Teslimat başarıyla tamamlandı.'
    },
    {
        id: 2,
        shipmentNumber: 'SHP-1002',
        orderNumber: 'ORD-7846',
        customerName: 'Anadolu Otomotiv Ltd.',
        shipmentDate: '2025-04-07',
        estimatedDeliveryDate: '2025-04-12',
        actualDeliveryDate: null,
        status: 'InTransit',
        carrier: 'MNG Kargo',
        trackingNumber: 'MNG987654321',
        totalWeight: 180,
        totalVolume: 0.8,
        totalItems: 8,
        deliveryAddress: 'Ankara, Türkiye',
        notes: 'Sevkiyat yolda, zamanında teslim edilecek.'
    },
    {
        id: 3,
        shipmentNumber: 'SHP-1003',
        orderNumber: 'ORD-7847',
        customerName: 'Yıldız Makine San.',
        shipmentDate: '2025-04-08',
        estimatedDeliveryDate: '2025-04-15',
        actualDeliveryDate: null,
        status: 'Preparing',
        carrier: 'Yurtiçi Kargo',
        trackingNumber: 'YK567891234',
        totalWeight: 320,
        totalVolume: 1.5,
        totalItems: 22,
        deliveryAddress: 'İzmir, Türkiye',
        notes: 'Sevkiyat hazırlanıyor.'
    },
    {
        id: 4,
        shipmentNumber: 'SHP-1004',
        orderNumber: 'ORD-7848',
        customerName: 'Doğu Otomotiv A.Ş.',
        shipmentDate: '2025-04-01',
        estimatedDeliveryDate: '2025-04-08',
        actualDeliveryDate: '2025-04-10',
        status: 'Delayed',
        carrier: 'PTT Kargo',
        trackingNumber: 'PTT246813579',
        totalWeight: 150,
        totalVolume: 0.6,
        totalItems: 10,
        deliveryAddress: 'Trabzon, Türkiye',
        notes: 'Teslimat gecikmeli olarak tamamlandı.'
    },
    {
        id: 5,
        shipmentNumber: 'SHP-1005',
        orderNumber: 'ORD-7849',
        customerName: 'Batı Sanayi Ltd.',
        shipmentDate: '2025-04-10',
        estimatedDeliveryDate: '2025-04-18',
        actualDeliveryDate: null,
        status: 'Scheduled',
        carrier: 'UPS',
        trackingNumber: 'UPS135792468',
        totalWeight: 280,
        totalVolume: 1.3,
        totalItems: 18,
        deliveryAddress: 'Antalya, Türkiye',
        notes: 'Sevkiyat planlandı, hazırlık aşamasında.'
    }
];

const statusOptions = [
    { name: 'Teslim Edildi', code: 'Delivered' },
    { name: 'Taşıma Halinde', code: 'InTransit' },
    { name: 'Hazırlanıyor', code: 'Preparing' },
    { name: 'Gecikmeli', code: 'Delayed' },
    { name: 'Planlandı', code: 'Scheduled' }
];

const carrierOptions = [
    { name: 'Aras Kargo', code: 'Aras Kargo' },
    { name: 'MNG Kargo', code: 'MNG Kargo' },
    { name: 'Yurtiçi Kargo', code: 'Yurtiçi Kargo' },
    { name: 'PTT Kargo', code: 'PTT Kargo' },
    { name: 'UPS', code: 'UPS' }
];

const ShipmentsDeliveriesPage = () => {
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        shipmentNumber: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
        },
        orderNumber: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
        },
        customerName: {
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        },
        status: { value: null, matchMode: FilterMatchMode.EQUALS },
        carrier: { value: null, matchMode: FilterMatchMode.EQUALS }
    });
    const [activeIndex, setActiveIndex] = useState(0);
    const [summaryData, setSummaryData] = useState({
        totalShipments: 0,
        delivered: 0,
        inTransit: 0,
        preparing: 0,
        delayed: 0,
        scheduled: 0
    });

    useEffect(() => {
        setShipments(mockShipments);
        setLoading(false);
 
        const summary = {
            totalShipments: mockShipments.length,
            delivered: mockShipments.filter((s) => s.status === 'Delivered').length,
            inTransit: mockShipments.filter((s) => s.status === 'InTransit').length,
            preparing: mockShipments.filter((s) => s.status === 'Preparing').length,
            delayed: mockShipments.filter((s) => s.status === 'Delayed').length,
            scheduled: mockShipments.filter((s) => s.status === 'Scheduled').length
        };
        setSummaryData(summary);
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };

        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const header = (
        <div className="flex justify-content-between align-items-center">
            <div className="flex align-items-center">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Ara..." />
                </span>
            </div>
            <div className="flex gap-2">
                <Button label="Excel Export" icon="pi pi-file-excel" severity="success" />
                <Button label="PDF Export" icon="pi pi-file-pdf" severity="warning" />
            </div>
        </div>
    );

    const statusBodyTemplate = (rowData) => {
        let severity = '';
        let statusText = '';

        switch (rowData.status) {
            case 'Delivered':
                severity = 'success';
                statusText = 'Teslim Edildi';
                break;
            case 'InTransit':
                severity = 'info';
                statusText = 'Taşıma Halinde';
                break;
            case 'Preparing':
                severity = 'warning';
                statusText = 'Hazırlanıyor';
                break;
            case 'Delayed':
                severity = 'danger';
                statusText = 'Gecikmeli';
                break;
            case 'Scheduled':
                severity = 'primary';
                statusText = 'Planlandı';
                break;
            default:
                severity = 'info';
                statusText = rowData.status;
        }

        return <Tag value={statusText} severity={severity} />;
    };

    const deliveryDateTemplate = (rowData) => {
        if (!rowData.actualDeliveryDate) return '-';

        const estimated = new Date(rowData.estimatedDeliveryDate);
        const actual = new Date(rowData.actualDeliveryDate);
        const diffDays = Math.round((actual - estimated) / (1000 * 60 * 60 * 24));

        if (diffDays > 0) {
            return (
                <div>
                    {dateOnlyBodyTemplate(rowData, 'actualDeliveryDate')}
                    <span className="ml-2 text-red-500">(+{diffDays} gün)</span>
                </div>
            );
        } else if (diffDays < 0) {
            return (
                <div>
                    {dateOnlyBodyTemplate(rowData, 'actualDeliveryDate')}
                    <span className="ml-2 text-green-500">({diffDays} gün)</span>
                </div>
            );
        } else {
            return (
                <div>
                    {dateOnlyBodyTemplate(rowData, 'actualDeliveryDate')}
                    <span className="ml-2 text-blue-500">(Zamanında)</span>
                </div>
            );
        }
    };

    const renderSummaryCards = () => {
        return (
            <div className="grid mb-4">
                <div className="col-12 md:col-6 lg:col-2">
                    <Card className="bg-blue-50">
                        <div className="flex justify-content-between">
                            <div>
                                <span className="block text-500 font-medium mb-3">Toplam Sevkiyat</span>
                                <div className="text-900 font-medium text-xl">{summaryData.totalShipments}</div>
                            </div>
                            <div className="flex align-items-center justify-content-center bg-blue-100 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                <i className="pi pi-truck text-blue-500 text-xl" />
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-12 md:col-6 lg:col-2">
                    <Card className="bg-green-50">
                        <div className="flex justify-content-between">
                            <div>
                                <span className="block text-500 font-medium mb-3">Teslim Edilen</span>
                                <div className="text-900 font-medium text-xl">{summaryData.delivered}</div>
                            </div>
                            <div className="flex align-items-center justify-content-center bg-green-100 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                <i className="pi pi-check-circle text-green-500 text-xl" />
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-12 md:col-6 lg:col-2">
                    <Card className="bg-cyan-50">
                        <div className="flex justify-content-between">
                            <div>
                                <span className="block text-500 font-medium mb-3">Taşıma Halinde</span>
                                <div className="text-900 font-medium text-xl">{summaryData.inTransit}</div>
                            </div>
                            <div className="flex align-items-center justify-content-center bg-cyan-100 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                <i className="pi pi-send text-cyan-500 text-xl" />
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-12 md:col-6 lg:col-2">
                    <Card className="bg-yellow-50">
                        <div className="flex justify-content-between">
                            <div>
                                <span className="block text-500 font-medium mb-3">Hazırlanıyor</span>
                                <div className="text-900 font-medium text-xl">{summaryData.preparing}</div>
                            </div>
                            <div className="flex align-items-center justify-content-center bg-yellow-100 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                <i className="pi pi-box text-yellow-500 text-xl" />
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-12 md:col-6 lg:col-2">
                    <Card className="bg-red-50">
                        <div className="flex justify-content-between">
                            <div>
                                <span className="block text-500 font-medium mb-3">Gecikmeli</span>
                                <div className="text-900 font-medium text-xl">{summaryData.delayed}</div>
                            </div>
                            <div className="flex align-items-center justify-content-center bg-red-100 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                <i className="pi pi-exclamation-circle text-red-500 text-xl" />
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-12 md:col-6 lg:col-2">
                    <Card className="bg-purple-50">
                        <div className="flex justify-content-between">
                            <div>
                                <span className="block text-500 font-medium mb-3">Planlandı</span>
                                <div className="text-900 font-medium text-xl">{summaryData.scheduled}</div>
                            </div>
                            <div className="flex align-items-center justify-content-center bg-purple-100 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                <i className="pi pi-calendar text-purple-500 text-xl" />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        );
    };

    const renderShipmentsTable = () => {
        return (
            <DataTable
                value={shipments}
                paginator
                className="p-datatable-gridlines"
                showGridlines
                rows={10}
                dataKey="id"
                loading={loading}
                filters={filters}
                filterDisplay="menu"
                globalFilterFields={['shipmentNumber', 'orderNumber', 'customerName', 'carrier', 'trackingNumber']}
                header={header}
                emptyMessage="Sevkiyat kaydı bulunamadı."
                rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]}
                tableStyle={{ minWidth: '50rem' }}
            >
                <Column field="shipmentNumber" header="Sevkiyat No" filter filterPlaceholder="Sevkiyat no ara..." sortable style={{ width: '120px' }} />
                <Column field="orderNumber" header="Sipariş No" filter filterPlaceholder="Sipariş no ara..." sortable style={{ width: '120px' }} />
                <Column field="customerName" header="Müşteri" filter filterPlaceholder="Müşteri ara..." sortable style={{ width: '180px' }} />
                <Column field="shipmentDate" header="Sevkiyat Tarihi" body={(rowData) => dateOnlyBodyTemplate(rowData, 'shipmentDate')} sortable style={{ width: '130px' }} />
                <Column field="estimatedDeliveryDate" header="Tahmini Teslimat" body={(rowData) => dateOnlyBodyTemplate(rowData, 'estimatedDeliveryDate')} sortable style={{ width: '130px' }} />
                <Column field="actualDeliveryDate" header="Gerçekleşen Teslimat" body={deliveryDateTemplate} sortable style={{ width: '150px' }} />
                <Column
                    field="status"
                    header="Durum"
                    body={statusBodyTemplate}
                    filter
                    filterElement={(options) => <Dropdown value={options.value} options={statusOptions} onChange={(e) => options.filterCallback(e.value)} optionLabel="name" optionValue="code" placeholder="Tümü" className="p-column-filter" />}
                    sortable
                    style={{ width: '130px' }}
                />
                <Column
                    field="carrier"
                    header="Kargo Firması"
                    filter
                    filterElement={(options) => <Dropdown value={options.value} options={carrierOptions} onChange={(e) => options.filterCallback(e.value)} optionLabel="name" optionValue="code" placeholder="Tümü" className="p-column-filter" />}
                    sortable
                    style={{ width: '130px' }}
                />
                <Column field="trackingNumber" header="Takip No" filter filterPlaceholder="Takip no ara..." sortable style={{ width: '130px' }} />
                <Column field="totalItems" header="Toplam Kalem" sortable style={{ width: '120px' }} />
                <Column field="deliveryAddress" header="Teslimat Adresi" sortable style={{ width: '180px' }} />
            </DataTable>
        );
    };

    const renderDeliveryPerformance = () => {
        // In a real application, this would show charts and performance metrics
        return (
            <div className="p-4">
                <div className="text-center mb-5">
                    <h3>Teslimat Performansı</h3>
                    <p className="text-500">Bu bölüm gerçek uygulamada teslimat performans grafiklerini ve metriklerini gösterecektir.</p>
                </div>

                <div className="grid">
                    <div className="col-12 md:col-6">
                        <Card title="Zamanında Teslimat Oranı">
                            <div className="flex align-items-center justify-content-center" style={{ height: '200px' }}>
                                <div className="text-center">
                                    <div className="text-5xl font-bold mb-3 text-primary">85%</div>
                                    <p className="m-0">Zamanında teslimat oranı</p>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="col-12 md:col-6">
                        <Card title="Ortalama Teslimat Süresi">
                            <div className="flex align-items-center justify-content-center" style={{ height: '200px' }}>
                                <div className="text-center">
                                    <div className="text-5xl font-bold mb-3 text-primary">4.2</div>
                                    <p className="m-0">Ortalama teslimat süresi (gün)</p>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">Sevkiyat ve Teslimatlar</h1>
            </div>

            {renderSummaryCards()}

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                <TabPanel header="Sevkiyat Listesi">
                    <Card>{renderShipmentsTable()}</Card>
                </TabPanel>
                <TabPanel header="Teslimat Performansı">{renderDeliveryPerformance()}</TabPanel>
            </TabView>
        </div>
    );
};

export default ShipmentsDeliveriesPage;
